import React from "react";
import { IoImageOutline } from "react-icons/io5";
import { Document, Page, pdfjs } from "react-pdf";
import { Link } from "../Link";
import { Lazy } from "@/components/Lazy";
import { checkValidURL, formatClientUrl } from "@/utils/formHelper";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { isClient } from "@/utils/browser";
import { PreviewLayout } from ".";
import { Typography } from "../Typography";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Button } from "../Button";

import "./styles.scss";

// Polyfill for Promise.withResolvers to avoid errors in old mobile browsers
if (typeof Promise.withResolvers === "undefined") {
  Promise.withResolvers = function <T>() {
    let resolve: (value: T | PromiseLike<T>) => void;
    let reject: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve: resolve!, reject: reject! };
  };
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PreviewPdf(props: QRProps<"pdf">) {
  const t = props.previewTranslations["pdf"];
  const qrEditorContext = React.useContext<QREditorContextProps<"pdf">>(QREditorContext);
  const data = props?.data || qrEditorContext?.qrData.data;
  const SHOW_MODAL_WIDTH = 888;

  const isInitPreview = props?.initPreview;

  const downloadPDF = () => {
    const downloadLink = document.createElement("a");
    downloadLink.href = data?.pdfFileUrl;
    downloadLink.download = data.pdfName || data?.pdfFileName || "pdf_download.pdf";
    downloadLink.click();
  };

  const showPDF = () => {
    if (typeof window === "undefined") return;
    const pdfWindow = window.open("");
    pdfWindow.document.write(
      `<iframe src="${data?.pdfFileUrl}" width="100%" height="100%" style="border: 0"></iframe>`
    );
    pdfWindow.document.body.style.margin = "0px";
  };

  const onOpenPdf = () => {
    if (!data?.pdfFileUrl) return;

    if (isClient() && window.innerWidth < SHOW_MODAL_WIDTH) {
      downloadPDF();
    } else {
      showPDF();
    }
  };

  return (
    <PreviewLayout
      page="pdf"
      dataQA="preview-pdf-container"
      backgroundColor={data?.themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <Typography
        Tag="p"
        spacing={4}
        classname="body-s u-bold pdf-company-name"
      >
        {data?.companyName}
      </Typography>

      <div className="pdf-content">
        <Typography
          Tag="p"
          spacing={4}
          size="xl"
          classname="u-bold pdf-name"
        >
          {data?.pdfName}
        </Typography>
        <Typography
          Tag="p"
          spacing={4}
          classname="body-s pdf-description"
        >
          {data?.pdfDescription}
        </Typography>
        <div className={`u-card u-spacing-3 pdf-card ${isInitPreview ? "initPreview" : ""}`}>
          {isInitPreview ? (
            <div className="pdf-empty">
              <Lazy placeholderClass="skeleton-placeholder__pdf__image">
                <img
                  src={data?.logoUrl}
                  alt="pdf menu preview"
                />
              </Lazy>
            </div>
          ) : (
            <>
              {data?.pdfFileUrl ? (
                <div style={{ height: "100%" }}>
                  <Document
                    className="pdf-box"
                    file={data?.pdfFileUrl}
                    externalLinkTarget="_blank"
                    onLoadError={(error) => console.error("Error loading PDF:", error)}
                    loading={
                      <div
                        style={{
                          width: "100%",
                          height: "14rem",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "red",
                        }}
                      >
                        <ButtonLib
                          shape="ghost"
                          isLoading
                          ariaLabel="Loading PDF"
                          dataQA="pdf-loading-button"
                        />
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                </div>
              ) : (
                <div className="pdf-empty">
                  <IoImageOutline style={{ color: "white", height: "100%", width: "80%" }} />
                </div>
              )}
            </>
          )}
        </div>
        <Button
          className="pdf-see"
          onClick={onOpenPdf}
          label={t?.preview_button_text}
          buttonColor={data?.themeColor?.buttonColor}
        />
      </div>
      <Typography
        Tag="p"
        spacing={6}
        classname="u-bold body-s pdf-url"
      >
        {data?.websiteUrl && (
          <Link
            externalLink
            className="Preview-layout__link"
            href={checkValidURL(data?.websiteUrl)}
            prefetch={false}
          >
            {formatClientUrl(data?.websiteUrl)}
          </Link>
        )}
      </Typography>
    </PreviewLayout>
  );
}
